import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Avatar,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Person,
  CalendarToday,
  Assignment,
  Send,
  Visibility,
  Share,
  ContentCopy,
  ThumbUp,
  ThumbUpOutlined
} from '@mui/icons-material';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { getUserProfileByUsername } from '../services/userService';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import { likeUserProfile, hasUserLikedProfile } from '../services/likeService';

const PublicUserProfile = () => {
  const { username } = useParams();
  const [user] = useAuthState(auth);
  const [userProfile, setUserProfile] = useState(null);
  const [userStats, setUserStats] = useState({
    totalForms: 0,
    totalSubmissions: 0,
    totalLikes: 0,
    recentForms: [],
    joinDate: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);

  useEffect(() => {
    const loadUserProfile = async () => {
      if (!username) return;

      try {
        setLoading(true);
        
        // Get user profile by username
        const profile = await getUserProfileByUsername(username);
        
        if (!profile) {
          setError('User not found');
          return;
        }

        setUserProfile(profile);
        setUserStats((prev) => ({ ...prev, totalLikes: profile.likes || 0 }));
        
        // Load user statistics
        await loadUserStats(profile.uid);
        
        if (user && user.uid !== profile.uid) {
          const liked = await hasUserLikedProfile(profile.uid, user.uid);
          setLiked(liked);
        }
        
      } catch (err) {
        console.error('Error loading user profile:', err);
        setError('Failed to load user profile');
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
    // eslint-disable-next-line
  }, [username, user]);

  const loadUserStats = async (userId) => {
    try {
      // Get forms created by user
      const formsQuery = query(
        collection(db, 'forms'),
        where('createdBy', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      
      const formsSnapshot = await getDocs(formsQuery);
      const recentForms = formsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Get submissions count
      const submissionsQuery = query(
        collection(db, 'submissions'),
        where('userId', '==', userId)
      );
      
      const submissionsSnapshot = await getDocs(submissionsQuery);
      
      // Calculate total views (sum of views from all forms)
      const totalViews = recentForms.reduce((sum, form) => sum + (form.views || 0), 0);

      setUserStats({
        totalForms: formsSnapshot.docs.length,
        totalSubmissions: submissionsSnapshot.docs.length,
        totalLikes: totalViews,
        recentForms,
        joinDate: userProfile?.createdAt
      });
      
    } catch (err) {
      console.error('Error loading user stats:', err);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${userProfile.displayName || userProfile.username}'s Profile`,
          url: url
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback to clipboard
      handleCopyLink();
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link');
    }
  };

  const handleLike = async () => {
    if (!user || !userProfile || user.uid === userProfile.uid || liked) return;
    setLikeLoading(true);
    try {
      await likeUserProfile(userProfile.uid, user.uid);
      setLiked(true);
      setUserStats((prev) => ({ ...prev, totalLikes: prev.totalLikes + 1 }));
    } catch (err) {
      // Optionally show error
    } finally {
      setLikeLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return 'Unknown';
    const now = new Date();
    const date = new Date(dateString);
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 30) return `${diffDays} days ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>Loading profile...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!userProfile) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          User profile not found.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 } }}>
      {/* Profile Header */}
      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'center', sm: 'flex-start' }, gap: 3 }}>
          <Avatar
            src={userProfile.photoURL}
            sx={{ width: { xs: 80, sm: 100 }, height: { xs: 80, sm: 100 } }}
            imgProps={{
              crossOrigin: 'anonymous',
              referrerPolicy: 'no-referrer'
            }}
          >
            <Person sx={{ fontSize: 48 }} />
          </Avatar>
          
          <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
            <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.8rem', sm: '2.125rem' } }}>
              {userProfile.displayName || userProfile.username}
            </Typography>
            
            <Typography variant="h6" color="text.secondary" sx={{ fontFamily: 'monospace', mb: 2 }}>
              @{userProfile.username}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', sm: 'flex-start' }, gap: 1, mb: 2 }}>
              <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Joined {formatDate(userProfile.createdAt)}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: { xs: 'center', sm: 'flex-start' }, mb: 2 }}>
              <Tooltip title={liked ? 'You liked this profile' : user && user.uid !== userProfile.uid ? 'Like this profile' : 'Sign in to like'}>
                <span>
                  <IconButton onClick={handleLike} disabled={!user || user.uid === userProfile.uid || liked || likeLoading} color={liked ? 'primary' : 'default'}>
                    {liked ? <ThumbUp /> : <ThumbUpOutlined />}
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant="body2" color="text.secondary">
                {userStats.totalLikes} {userStats.totalLikes === 1 ? 'Like' : 'Likes'}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
              <Tooltip title={copied ? 'Copied!' : 'Copy profile link'}>
                <IconButton onClick={handleCopyLink} size="small">
                  <ContentCopy />
                </IconButton>
              </Tooltip>
              <Tooltip title="Share profile">
                <IconButton onClick={handleShare} size="small">
                  <Share />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Assignment sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" color="primary">
                {userStats.totalForms}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Forms Created
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Send sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" color="success.main">
                {userStats.totalSubmissions}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Submissions
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ThumbUp sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h4" color="info.main">
                {userStats.totalLikes}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Likes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Forms */}
      {userStats.recentForms.length > 0 && (
        <Paper elevation={3} sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h5" gutterBottom>
            Recent Forms
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {userStats.recentForms.map((form, index) => (
            <Box key={form.id} sx={{ mb: index < userStats.recentForms.length - 1 ? 2 : 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {form.title || form.name || 'Untitled Form'}
                </Typography>
                <Chip 
                  label={getTimeAgo(form.createdAt)} 
                  size="small" 
                  variant="outlined" 
                />
              </Box>
              
              {form.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {form.description}
                </Typography>
              )}
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">
                  <Visibility sx={{ fontSize: 14, mr: 0.5 }} />
                  {form.views || 0} views
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <Send sx={{ fontSize: 14, mr: 0.5 }} />
                  {form.submissions || 0} submissions
                </Typography>
              </Box>
              
              {index < userStats.recentForms.length - 1 && <Divider sx={{ mt: 2 }} />}
            </Box>
          ))}
        </Paper>
      )}
    </Container>
  );
};

export default PublicUserProfile;
